import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { financials, buildings } from '../services/api';
import api from '../services/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const CAT_COLORS = [
  '#3b82f6','#10b981','#8b5cf6','#f59e0b',
  '#ef4444','#06b6d4','#ec4899','#84cc16',
];

function BarRow({ category, paid, budget, total, pct, color }) {
  const overBudget = budget > 0 && paid > budget;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="font-medium text-gray-800 truncate max-w-[40%]">{category}</span>
        <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
          {budget > 0 && (
            <span className={overBudget ? 'text-red-600 font-semibold' : 'text-gray-400'}>
              {overBudget ? '⚠ ' : ''}Budget ${budget.toLocaleString()}
            </span>
          )}
          <span className="font-semibold text-gray-900">${paid.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span>
        </div>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: overBudget ? '#ef4444' : color }} />
      </div>
      {budget > 0 && (
        <div className="flex justify-between text-xs text-gray-400 mt-0.5">
          <span>{Math.round(pct)}% of budget used</span>
          {overBudget
            ? <span className="text-red-500 font-medium">${(paid - budget).toLocaleString()} over</span>
            : <span className="text-green-600">${(budget - paid).toLocaleString()} remaining</span>}
        </div>
      )}
    </div>
  );
}

function MonthlyChart({ trend }) {
  const max = Math.max(...trend.map(t => t.amount), 1);
  return (
    <div className="flex items-end gap-1 h-24">
      {trend.map((t, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full bg-blue-500 rounded-t transition-all duration-500"
            style={{ height: `${(t.amount / max) * 80}px`, minHeight: t.amount > 0 ? 4 : 0 }} />
          <span className="text-xs text-gray-400">{MONTHS[i]}</span>
        </div>
      ))}
    </div>
  );
}

export default function FinancialReports() {
  const [buildingList, setBuildingList] = useState([]);
  const [buildingId, setBuildingId]   = useState('');
  const [year, setYear]               = useState(new Date().getFullYear());
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [budgetEdit, setBudgetEdit]   = useState(false);
  const [budgetDraft, setBudgetDraft] = useState({});
  const [savingBudget, setSavingBudget] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    financials.summary({ building_id: buildingId || undefined, year })
      .then(r => { setData(r.data); setBudgetDraft(r.data.budgets || {}); })
      .catch((e) => {
        if (e.response?.status !== 404) toast.error('Failed to load financials');
        setData({ year, total_spend: 0, total_pending: 0, anomalies: 0, invoice_count: 0, by_category: [], monthly_trend: Array.from({length:12},(_,i)=>({month:i+1,amount:0})), budgets: {} });
      })
      .finally(() => setLoading(false));
  }, [buildingId, year]);

  useEffect(() => {
    buildings.list().then(r => setBuildingList(r.data)).catch(() => {});
    load();
  }, [load]);

  const handleExport = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ year });
    if (buildingId) params.set('building_id', buildingId);
    // Use fetch with auth header then trigger download
    api.get(`/financials/export?${params}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a   = document.createElement('a');
        a.href    = url;
        a.download = `financials_${year}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Export failed'));
  };

  const saveBudget = async () => {
    if (!buildingId) { toast.error('Select a specific building to set a budget'); return; }
    setSavingBudget(true);
    try {
      await financials.saveBudget({ building_id: buildingId, budgets: budgetDraft });
      toast.success('Budget saved');
      setBudgetEdit(false);
      load();
    } catch { toast.error('Failed to save budget'); }
    finally { setSavingBudget(false); }
  };

  const maxAmount = data ? Math.max(...data.by_category.map(c => Math.max(c.paid, c.budget || 0)), 1) : 1;

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1>
          <p className="text-gray-500 text-sm mt-1">YTD spend, budget tracking, and accountant export.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={buildingId} onChange={e => setBuildingId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Buildings</option>
            {buildingList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handleExport}
            className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !data ? null : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-sm text-gray-500">Total Paid ({year})</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${data.total_spend.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-sm text-gray-500">Pending / Approved</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">
                ${data.total_pending.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-sm text-gray-500">Invoices</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.invoice_count}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-sm text-gray-500">Anomalies Flagged</p>
              <p className={`text-2xl font-bold mt-1 ${data.anomalies > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {data.anomalies}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Spend by category */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-semibold text-gray-900">Spend by Category</h2>
                {buildingId && (
                  <button onClick={() => setBudgetEdit(!budgetEdit)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    {budgetEdit ? 'Cancel' : 'Set Budget'}
                  </button>
                )}
              </div>

              {budgetEdit ? (
                <div className="space-y-3">
                  <p className="text-xs text-gray-400 mb-3">Enter annual budget per category:</p>
                  {data.by_category.map(cat => (
                    <div key={cat.category} className="flex items-center gap-3">
                      <span className="text-sm text-gray-700 flex-1 truncate">{cat.category}</span>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          value={budgetDraft[cat.category] || ''}
                          onChange={e => setBudgetDraft(p => ({ ...p, [cat.category]: e.target.value }))}
                          className="w-32 pl-6 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  ))}
                  <button onClick={saveBudget} disabled={savingBudget}
                    className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg disabled:opacity-60">
                    {savingBudget ? 'Saving…' : 'Save Budget'}
                  </button>
                </div>
              ) : (
                data.by_category.length === 0 ? (
                  <p className="text-gray-400 text-sm">No invoices found for {year}.</p>
                ) : (
                  data.by_category.map((cat, i) => (
                    <BarRow
                      key={cat.category}
                      category={cat.category}
                      paid={cat.paid}
                      budget={cat.budget}
                      total={cat.total}
                      pct={cat.budget > 0 ? (cat.paid / cat.budget) * 100 : (cat.paid / maxAmount) * 100}
                      color={CAT_COLORS[i % CAT_COLORS.length]}
                    />
                  ))
                )
              )}
            </div>

            {/* Monthly trend */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="font-semibold text-gray-900 mb-5">Monthly Spend ({year})</h2>
              <MonthlyChart trend={data.monthly_trend} />
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Average / month</span>
                  <span className="font-semibold text-gray-900">
                    ${(data.total_spend / 12).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CSV export note */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3 mb-6">
            <svg className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Accountant export</span> — the CSV includes GL Account / Category, vendor, invoice number, date, and amount in a format compatible with QuickBooks and Xero manual import.{' '}
              <button onClick={handleExport} className="underline font-medium">Download now →</button>
            </p>
          </div>
        </>
      )}
    </div>
  );
}
